import { action, computed, makeObservable, observable, override, reaction, runInAction } from 'mobx';
import moment from 'moment';
import FiltrosPautaJulgamento from '../components/FiltrosPautaJulgamento';
import Configuracao from '../util/Configuracao';
import Store from './Store';
import { MEIO_JULGAMENTO_SESSAO } from '@/dominio/MeioJulgamentoSessao';
import { LOCALSTORAGE_STORAGE } from '@/util/StorageUtil';

/**
 * Classe utilizada para guardar e manipular o estado da aplicação. Deve-se
 * criar uma para cada contexto ou entidade.
 */
export default class PautaJulgamentoStore extends Store {
    carregandoPauta = false;
    carregandoPartesPautaveis = false;
    carregandoJulgamentosAnterioresPautaveis = false;
    carregandoUltimosAndamentosSessao = false;
    carregandoUltimosAndamentosPautaveis = false;
    idPautaSelecionada;
    pautaSelecionada;
    processosPautaveis = [];
    mapaProcessosPartesPautaveis = {};
    dataLimiteEnvioPauta;

    constructor(props) {
        super(props);

        makeObservable(this, {
            carregandoPauta: observable,
            carregandoPartesPautaveis: observable,
            carregandoJulgamentosAnterioresPautaveis: observable,
            carregandoUltimosAndamentosSessao: observable,
            carregandoUltimosAndamentosPautaveis: observable,
            idPautaSelecionada: observable,
            pautaSelecionada: observable,
            processosPautaveis: observable,
            mapaProcessosPartesPautaveis: observable,
            dataLimiteEnvioPauta: observable,
            objetoLocalStorage: computed,
            resetAtributosProcessos: action,
            descricaoProcessosEmExibicao: computed,
            carregandoPartes: computed,
            carregandoJulgamentosAnteriores: computed,
            processosEmExibicao: computed,
            indiceProcessoExpandido: computed,
            exibirProcessoPeloIndice: action,
            expandirProximoProcesso: action,
            expandirProcessoAnterior: action,
            processosDisponiveis: computed,
            processosDisponiveisFiltrados: computed,
            sessaoSelecionada: computed,
            idSessaoSelecionada: computed,
            sessoesJulgamento: computed,
            processosPautaSelecionadaFiltrados: computed,
            inicializarPautaJulgamento: action,
            carregarInformacoesPauta: action,
            carregarPartesPautaveis: action,
            carregarProcessosPautaveis: action,
            carregarProcessosPautaveisSemCache: action,
            configurarProcessosPautaveis: action,
            carregarDetalhes: action,
            origemPautaSelecionada: computed,
            origemPautaSelecionadaPJe: computed,
            origemPautaSelecionadaSIJ: computed,
            carregarDetalhesProcessoSemSessao: action,
            incluirProcessoPauta: action,
            atualizarQuantidadeProcessosListaSessoes: action,
            removerProcessoPauta: action,
            incluirProcessoArrayPautaveis: action,
            removerProcessoArrayPautaveis: action,
            incluirProcessoArraySessao: action,
            removerProcessoArraySessao: action,
            exibirListaProcessos: action,
            carregarJulgamentosAnterioresPautaveis: action,
            carregandoUltimosAndamentos: computed,
            carregarUltimosAndamentosPautaveis: action,
            carregarUltimosAndamentosSessaoSelecionada: action,
            reset: override,
            processosSessao: override,
            processosFiltrados: override,
            mapaProcessosPartes: override,
            mapaProcessosPartesPautaSelecionada: computed
        });

        runInAction(() => (this.idProcessosEmExibicao = 'A'));
        this.createReaction(
            reaction(
                () => this.idProcessosEmExibicao,
                () => (this.processoScroll = undefined)
            )
        );
        this.createReaction(
            reaction(
                () => this.processoExpandido,
                (processoExpandido) => {
                    if (processoExpandido) {
                        this.carregarDetalhes(processoExpandido);
                    }
                }
            )
        );
    }

    get storageType() {
        return LOCALSTORAGE_STORAGE;
    }

    async inicializar() {
        super.inicializar();
        this.inicializarFiltros(() => new FiltrosPautaJulgamento(this));
    }

    get objetoLocalStorage() {
        return {
            nomeStore: 'PautaJulgamentoStore',
            idPautaSelecionada: this.idPautaSelecionada,
            fecharProgressoAutomaticamente:
                this.fecharProgressoAutomaticamente === undefined ? false : this.fecharProgressoAutomaticamente
        };
    }

    reset() {
        super.reset();
        this.resetAtributosProcessos();
    }

    resetAtributosProcessos() {
        this.carregandoPauta = false;
        this.carregandoPartesPautaveis = false;
        this.carregandoJulgamentosAnterioresPautaveis = false;
        this.carregandoUltimosAndamentosSessao = false;
        this.carregandoUltimosAndamentosPautaveis = false;
        this.idPautaSelecionada = undefined;
        this.pautaSelecionada = undefined;
        this.mapaProcessosPartesPautaveis = {};
        this.processoExpandido = undefined;
        this.processoModal = undefined;
        this.processoScroll = undefined;
        this.idProcessosEmExibicao = 'A';
        this.offsetScroll = {};
    }

    get opcoesProcessosEmExibicao() {
        let array = [];
        let ordem = 0;
        array.push({
            id: 'A',
            ordem: ordem++,
            descricao: 'disponíveis',
            descricaoSingular: 'Disponível',
            funcaoProcessos: () => this.processosDisponiveis,
            funcaoProcessosFiltrados: () => this.processosDisponiveisFiltrados
        });
        array.push({
            id: 'P',
            ordem: ordem++,
            descricao: 'em pauta/sessão',
            descricaoSingular: 'Em pauta/sessão',
            funcaoProcessos: () => this.processosPautaSelecionada,
            funcaoProcessosFiltrados: () => this.processosPautaSelecionadaFiltrados
        });
        return array;
    }

    get descricaoProcessosEmExibicao() {
        let opcaoProcessosEmExibicao = this.opcoesProcessosEmExibicao.find(
            (opcaoProcessos) => opcaoProcessos.id === this.idProcessosEmExibicao
        );
        return opcaoProcessosEmExibicao ? opcaoProcessosEmExibicao.descricao : '';
    }

    get carregandoPartes() {
        return this.sessaoStore.carregandoPartes || this.carregandoPartesPautaveis;
    }

    get carregandoJulgamentosAnteriores() {
        return this.sessaoStore.carregandoJulgamentosAnteriores || this.carregandoJulgamentosAnterioresPautaveis;
    }

    get processosEmExibicao() {
        return this.idProcessosEmExibicao === 'A'
            ? this.processosDisponiveisFiltrados
            : this.processosPautaSelecionadaFiltrados;
    }

    get indiceProcessoExpandido() {
        let indiceProcessoExpandido = -1;

        if (this.processoExpandido) {
            indiceProcessoExpandido = this.processosEmExibicao.findIndex(
                (registro) =>
                    // @ts-ignore
                    registro.processo.numeroCompleto.indexOf(this.processoExpandido.processo.numeroCompleto) !== -1
            );
        }

        return indiceProcessoExpandido;
    }

    exibirProcessoPeloIndice(index) {
        if (this.processosEmExibicao.length > 0) {
            index = index >= 0 ? index : 0;
            index = index > this.processosEmExibicao.length - 1 ? this.processosEmExibicao.length - 1 : index;
            this.processoExpandido = this.processosEmExibicao[index];
            this.processoScroll = this.processosEmExibicao[index];
        }
    }

    expandirProximoProcesso() {
        this.exibirProcessoPeloIndice(this.indiceProcessoExpandido + 1);
    }

    expandirProcessoAnterior() {
        this.exibirProcessoPeloIndice(this.indiceProcessoExpandido - 1);
    }

    // Sobrescreve o método processosSessao para exibir apenas os processos da pauta selecionada e os pautáveis
    // @see Store.processosSessao
    get processosSessao() {
        return this.processosPautaveis
            ? this.processosPautaveis.concat(this.processosPautaSelecionada)
            : this.processosPautaSelecionada;
    }

    get processosFiltrados() {
        return this.filtros && this.filtros.filtrarProcessos(this.processosSessao);
    }

    get processosDisponiveis() {
        return this.processosPautaveis
            ? this.processosPautaveis.filter(
                  (registro) =>
                      registro.tipoInclusao === 'APTO_PAUTA' ||
                      registro.tipoInclusao === 'APTO_JULGAMENTO' ||
                      registro.tipoInclusao === 'REMANESCENTE' ||
                      registro.tipoInclusao === 'AGRAVO_PROVIDO'
              )
            : [];
    }

    get processosDisponiveisFiltrados() {
        return this.filtros && this.filtros.filtrarProcessos(this.processosDisponiveis);
    }

    get sessaoSelecionada() {
        return this.sessaoStore.sessaoSelecionada;
    }

    get idSessaoSelecionada() {
        return this.sessaoStore && this.sessaoStore.idSessaoSelecionada;
    }

    get sessoesJulgamento() {
        return this.sessaoStore && this.sessaoStore.sessoesJulgamento;
    }

    // Não colocar @computed para não perder atualizações dos processos da sessão selecionada
    get processosPautaSelecionada() {
        let processos =
            this.sessaoSelecionada && this.sessaoSelecionada.processos ? this.sessaoSelecionada.processos : [];
        return processos.filter((registro) => registro.sessaoId === this.idPautaSelecionada);
    }

    get processosPautaSelecionadaFiltrados() {
        return this.filtros && this.filtros.filtrarProcessos(this.processosPautaSelecionada);
    }

    inicializarPautaJulgamento = async () => {
        await this.createWhenReaction(
            () => this.idSessaoSelecionada !== undefined && this.sessoesJulgamento && this.sessoesJulgamento.length > 0
        );
        if (!this.isPautaJulgamentoValida(this.idPautaSelecionada)) {
            runInAction(() => (this.idPautaSelecionada = this.idSessaoSelecionada.split('----')[0]));
        }
        if (this.idPautaSelecionada) {
            try {
                runInAction(() => (this.carregandoPauta = true));
                if (!this.pautaSelecionada || this.pautaSelecionada.id !== this.idPautaSelecionada) {
                    runInAction(() => (this.processoExpandido = undefined));
                    await this.carregarInformacoesPauta();
                    this.complementarInformacoesPautaSelecionada();
                    await this.carregarProcessosPautaveis();
                }
            } finally {
                runInAction(() => (this.carregandoPauta = false));
            }
        }
    };

    complementarInformacoesPautaSelecionada() {
        if (this.validarPautaSelecionada()) {
            this.sessaoStore.carregarPartesSessao(this.idPautaSelecionada);
            this.sessaoStore.carregarJulgamentosAnteriores();
            this.carregarPartesPautaveis();
            this.carregarJulgamentosAnterioresPautaveis();
            this.carregarUltimosAndamentosSessaoSelecionada();
            this.carregarUltimosAndamentosPautaveis();
        }
    }

    carregarInformacoesPauta = async () => {
        let pauta = await this.sessaoService.recuperarPautaJulgamento(this.idPautaSelecionada);
        if (pauta && pauta.id === this.idPautaSelecionada) {
            this.sessaoStore.configurarSessao(pauta);
            runInAction(() => (this.pautaSelecionada = pauta));
            await this.atualizarDataLimiteEnvioPauta();
        }
    };

    getDataInicioJulgamento() {
        return this.pautaSelecionada.tipoJulgamento === MEIO_JULGAMENTO_SESSAO.VIRTUAL.codigo
            ? this.pautaSelecionada.prazos.dataInicioVotacaoVirtual
            : this.pautaSelecionada.prazos.dataInicioSessaoPresencial;
    }

    async atualizarDataLimiteEnvioPauta() {
        let prazos = await this.sessaoService.recuperarPrazosPublicacaoPauta(this.getDataInicioJulgamento());
        runInAction(() => (this.dataLimiteEnvioPauta = moment(prazos.dataLimiteEnvio, 'DD-MM-YYYY-HH-mm-ss', true)));
    }

    validarPautaSelecionada() {
        return this.pautaSelecionada && this.pautaSelecionada.id === this.idPautaSelecionada;
    }

    async carregarPartesPautaveis() {
        this.carregandoPartesPautaveis = true;
        let partes = this.sessaoService.recuperarPartesPautaveis(this.idPautaSelecionada);
        partes
            .then((result) => {
                if (this.validarPautaSelecionada()) {
                    runInAction(
                        () =>
                            (this.mapaProcessosPartesPautaveis = this.getMapaProcessosPartes(
                                result,
                                this.idPautaSelecionada
                            ))
                    );
                }
            })
            .finally(() => {
                runInAction(() => (this.carregandoPartesPautaveis = false));
            });
        return partes;
    }

    get mapaProcessosPartes() {
        //pauta selecionada mais pautaveis
        return { ...this.mapaProcessosPartesPautaSelecionada, ...this.mapaProcessosPartesPautaveis };
    }

    get mapaProcessosPartesPautaSelecionada() {
        let mapaSessao = {};
        for (const key in this.sessaoStore.mapaProcessosPartesSessao) {
            const sessoes = this.sessaoStore.mapaProcessosPartesSessao[key].sessaoId.split('----');
            if (sessoes.includes(this.idPautaSelecionada)) {
                mapaSessao[key] = this.sessaoStore.mapaProcessosPartesSessao[key];
            }
        }
        return mapaSessao;
    }

    carregarProcessosPautaveis() {
        if (this.validarPautaSelecionada()) {
            this.carregandoProcessos = true;
            let processosPautaveis = this.sessaoService.recuperarPautaveis(this.idPautaSelecionada);
            processosPautaveis
                .then((result) => {
                    this.configurarProcessosPautaveis(result);
                })
                .finally(() => {
                    runInAction(() => (this.carregandoProcessos = false));
                });
            return processosPautaveis;
        }
    }

    carregarProcessosPautaveisSemCache() {
        return this.carregarProcessosPautaveis();
    }

    configurarProcessosPautaveis(processosPautaveis) {
        if (this.validarPautaSelecionada()) {
            this.sessaoStore.configurarProcessosSessao(processosPautaveis);
            this.atualizarProcessoExpandido(this.processoExpandido);
            this.processosPautaveis = processosPautaveis;
        }
    }

    isPautaJulgamentoValida(idPautaSelecionada) {
        return this.idSessaoSelecionada && this.idSessaoSelecionada.indexOf(idPautaSelecionada) !== -1;
    }

    async carregarDetalhes(registro) {
        if (registro) {
            if (registro.sessaoId) {
                await this.carregarDetalhesProcesso(registro);
            } else {
                await this.carregarDetalhesProcessoSemSessao(registro);
            }
        }
    }

    get origemPautaSelecionada() {
        return this.idPautaSelecionada && this.idPautaSelecionada.split('--')[0];
    }

    get origemPautaSelecionadaPJe() {
        return this.origemPautaSelecionada === Configuracao.ORIGEM_PJE;
    }

    get origemPautaSelecionadaSIJ() {
        return this.origemPautaSelecionada === Configuracao.ORIGEM_SIJ;
    }

    async carregarDetalhesProcessoSemSessao(registro) {
        if (registro?.processo?.numeroCompleto) {
            let resultado = await this.processoService.recuperarDetalhesProcesso(
                registro.processo.numeroCompleto,
                registro.processo.origem
            );
            let detalhes = resultado.data;
            await this.complementarMovimentosJulgamentosAnteriores(detalhes, registro);
            if (!detalhes.julgamentoAnterior && registro.detalhes?.julgamentoAnterior) {
                detalhes.julgamentoAnterior = registro.detalhes.julgamentoAnterior;
            }
            if (!detalhes.julgamentosAnteriores && registro.detalhes?.julgamentosAnteriores) {
                detalhes.julgamentosAnteriores = registro.detalhes.julgamentosAnteriores;
            }
            runInAction(() => (registro.detalhes = detalhes));
        }
    }

    async incluirProcessoPauta(registro) {
        await this.processoService.incluirProcessoEmPauta(this.idPautaSelecionada, registro, true);
        this.removerProcessoArrayPautaveis(registro.processo.numeroCompleto);
        await this.incluirProcessoArraySessao(registro.processo.numeroCompleto);
        this.atualizarQuantidadeProcessosListaSessoes();
    }

    atualizarQuantidadeProcessosListaSessoes() {
        const sessaoLista = this.sessaoStore.sessoesJulgamento.find(
            (sessao) => sessao.id === this.sessaoStore.idSessaoSelecionada
        );

        if (sessaoLista) {
            sessaoLista.quantidadeTotalProcessos = this.sessaoSelecionada?.processos?.length
                ? this.sessaoSelecionada.processos.length
                : 0;

            sessaoLista.quantidadesTotaisProcessos[this.idPautaSelecionada] = this.processosPautaSelecionada?.length
                ? this.processosPautaSelecionada.length
                : 0;
        }

        if (this.sessaoSelecionada) {
            this.sessaoSelecionada.quantidadeTotalProcessos = this.sessaoSelecionada?.processos?.length
                ? this.sessaoSelecionada.processos.length
                : 0;

            this.sessaoSelecionada.quantidadesTotaisProcessos[this.idPautaSelecionada] = this.processosPautaSelecionada
                ?.length
                ? this.processosPautaSelecionada.length
                : 0;
        }
    }

    async removerProcessoPauta(registro) {
        await this.processoService.removerProcessoPauta(this.idPautaSelecionada, registro);
        this.removerProcessoArraySessao(registro);
        await this.incluirProcessoArrayPautaveis(registro);
        this.atualizarQuantidadeProcessosListaSessoes();
    }

    async incluirProcessoArrayPautaveis(registro) {
        let novoRegistro = await this.processoService.recuperarProcessoPautavel(
            this.idPautaSelecionada,
            registro.processo.numeroCompleto
        );

        if (novoRegistro && this.processosPautaveis) {
            this.sessaoStore.configurarProcesso(novoRegistro);
            this.carregarDetalhesProcessoSemSessao(novoRegistro);
            runInAction(() => this.processosPautaveis.push(novoRegistro));
            this.atualizarProcessoExpandido(novoRegistro);
        }
    }

    removerProcessoArrayPautaveis(numeroProcesso) {
        this.processosPautaveis = this.processosPautaveis.filter(
            (regPautavel) => regPautavel.processo.numeroCompleto !== numeroProcesso
        );
    }

    async incluirProcessoArraySessao(numeroProcesso) {
        let novoRegistro = await this.sessaoService.recuperarProcessoSessao(this.idPautaSelecionada, numeroProcesso);
        this.sessaoStore.configurarProcesso(novoRegistro);
        this.carregarDetalhesProcesso(novoRegistro);
        runInAction(() => this.sessaoSelecionada.processos.push(novoRegistro));
        this.atualizarProcessoExpandido(novoRegistro);
    }

    removerProcessoArraySessao(registro) {
        this.sessaoSelecionada.processos = this.sessaoSelecionada.processos.filter(
            (regSessao) => regSessao.processo.numeroCompleto !== registro.processo.numeroCompleto
        );
    }

    isProcessoPautavel(registro) {
        return (
            this.processosPautaveis.findIndex(
                (reg) => reg.processo.numeroCompleto === registro.processo.numeroCompleto
            ) !== -1
        );
    }

    isProcessoEmPauta(registro) {
        return (
            this.processosPautaSelecionada.findIndex(
                (reg) => reg?.processo?.numeroCompleto === registro?.processo?.numeroCompleto
            ) !== -1
        );
    }

    exibirListaProcessos(processoScroll) {
        if (this.isProcessoEmPauta(processoScroll)) {
            this.idProcessosEmExibicao = 'P';
        } else {
            this.idProcessosEmExibicao = 'A';
        }
    }

    async carregarJulgamentosAnterioresPautaveis() {
        try {
            this.carregandoJulgamentosAnterioresPautaveis = true;
            const resultados = await this.sessaoService.recuperarJulgamentoAnteriorPautaveis(this.idPautaSelecionada);
            await this.createWhenReaction(() => !this.carregandoProcessos);

            if (this.validarPautaSelecionada() && resultados && resultados.length > 0) {
                resultados.forEach((resultado) => {
                    const registro = this.processosPautaveis.find(
                        (reg) => reg.processo.numeroCompleto === resultado.numeroCompleto
                    );

                    if (registro?.detalhes) {
                        runInAction(() => {
                            registro.detalhes.julgamentoAnterior = resultado.julgamentoAnterior;
                            registro.detalhes.julgamentosAnteriores = resultado.julgamentosAnteriores;
                        });
                    }
                });
            }
        } finally {
            runInAction(() => (this.carregandoJulgamentosAnterioresPautaveis = false));
        }
    }

    get carregandoUltimosAndamentos() {
        return this.carregandoUltimosAndamentosPautaveis || this.carregandoUltimosAndamentosSessao;
    }

    async carregarUltimosAndamentosPautaveis() {
        if (this.origemPautaSelecionadaSIJ) {
            try {
                this.carregandoUltimosAndamentosPautaveis = true;
                let andamentos = await this.sessaoService.recuperarUltimosAndamentosPautaveis(this.idPautaSelecionada);
                await this.createWhenReaction(() => !this.carregandoProcessos);
                if (this.validarPautaSelecionada()) {
                    this.configurarAndamentosProcessos(andamentos, this.processosPautaveis);
                }
            } finally {
                runInAction(() => (this.carregandoUltimosAndamentosPautaveis = false));
            }
        }
    }

    async carregarUltimosAndamentosSessaoSelecionada() {
        if (this.origemPautaSelecionadaSIJ) {
            try {
                runInAction(() => (this.carregandoUltimosAndamentosSessao = true));
                let andamentos = await this.sessaoService.recuperarUltimosAndamentosSessao(this.idPautaSelecionada);
                await this.createWhenReaction(() => this.sessaoSelecionada?.processos);
                if (this.validarPautaSelecionada()) {
                    this.configurarAndamentosProcessos(andamentos, this.sessaoSelecionada.processos);
                }
            } finally {
                runInAction(() => (this.carregandoUltimosAndamentosSessao = false));
            }
        }
    }

    configurarAndamentosProcessos(andamentos, processos) {
        if (
            this.validarPautaSelecionada() &&
            andamentos &&
            andamentos.length > 0 &&
            processos &&
            processos.length > 0
        ) {
            andamentos.forEach((andamento) => {
                let registro = processos.find((reg) => reg.numeroProcesso === andamento.numero);
                if (registro) {
                    runInAction(() => (registro.detalhes.ultimoAndamento = andamento.motivoAndamento));
                }
            });
        }
    }

    isDataSessaoPresencialFutura() {
        return !!(
            this.pautaSelecionada &&
            moment(this.pautaSelecionada.prazos.dataInicioSessaoPresencial, 'DD-MM-YYYY-HH-mm-ss').isAfter(moment())
        );
    }
}
